import { useEffect, useRef, useState } from 'react';

interface UseProgressiveImageProps {
  fullImageSrc?: string; // URL полного изображения
  isIntersecting?: boolean; // Флаг видимости через IntersectionObserver
  transitionDuration?: number; // Продолжительность плавного перехода в ms
}

interface UseProgressiveImageReturnProps {
  src: string | null;
  loading: boolean;
  loaded: boolean;
  isTransitioning: boolean;
}

/**
 * useProgressiveImage - кастомный React-хук для прогрессивной загрузки изображений
 * с поддержкой плавного перехода и отслеживания видимости через IntersectionObserver.
 *
 * @param {UseProgressiveImageProps} props - Настройки хука.
 * @param {string} props.fullImageSrc - URL полного изображения, которое будет загружено.
 * @param {boolean} [props.isIntersecting=true] - Флаг, указывающий, находится ли элемент в области видимости (для оптимизации загрузки изображений).
 * @param {number} [props.transitionDuration=200] - Длительность плавного перехода при смене изображений в миллисекундах.
 *
 * @returns {UseProgressiveImageReturnProps} - Возвращает объект со следующими свойствами:
 * - `src`: URL загруженного изображения или `null`, если изображение ещё не загружено.
 * - `loading`: булевое значение, указывающее на состояние загрузки изображения.
 * - `loaded`: булевое значение, показывающее, было ли изображение загружено.
 * - `isTransitioning`: булевое значение, показывающее, выполняется ли плавный переход между изображениями.
 */

export const useProgressiveImage = ({
  fullImageSrc,
  isIntersecting = true,
  transitionDuration = 200,
}: UseProgressiveImageProps): UseProgressiveImageReturnProps => {
  const isMounted = useRef(true);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  useEffect(() => {
    if (!isIntersecting || !fullImageSrc) {
      return;
    }

    const img = new Image();
    img.src = fullImageSrc;

    if (img.complete) {
      setSrc(fullImageSrc); // Если изображение в кэше, сразу установить fullImageSrc
      setLoaded(true); // Изображение загружено
      setLoading(false); // Лоадер не показывать
      setIsTransitioning(false); // Переход уже завершён
    } else {
      setLoading(true); // Устанавливаем лоадер только если изображение не загружено
    }

    return () => {
      isMounted.current = false;
    };
  }, [fullImageSrc, isIntersecting]);

  useEffect(() => {
    if (!isIntersecting || loaded || !fullImageSrc) {
      return;
    }

    const img = new Image();
    img.src = fullImageSrc;

    const handleLoad = () => {
      if (!isMounted.current) {
        return;
      }

      setSrc(fullImageSrc);
      setLoading(false);
      setLoaded(true);
      setTimeout(() => setIsTransitioning(false), transitionDuration);
    };

    const handleError = () => {
      if (!isMounted.current) {
        return;
      }

      setLoading(false);
    };

    img.onload = handleLoad;
    img.onerror = handleError;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [fullImageSrc, isIntersecting, transitionDuration, loaded]);

  return { src, loading, loaded, isTransitioning };
};
